import { fetchWithAuth, ensureFreshAccessToken } from './http';
import { AI_SERVICE_BASE_URL, AI_STREAMING_BASE_URL, DATA_SERVICE_BASE_URL } from '../config/runtime';
import { AudioContentItem, AudioFormat, AudioGenerationResult, GenerateAudioPayload, TtsVoice } from '../types';

function normalizeAudio(raw: any): AudioContentItem {
  return {
    id: raw.id,
    documentId: raw.documentId ?? raw.document_id ?? raw.audioDocumentId ?? raw.audio_document_id,
    title: raw.title,
    description: raw.description,
    format: (raw.format ?? 'podcast') as AudioFormat,
    status: raw.status,
    createdAt: raw.createdAt ?? raw.created_at,
    durationSeconds: raw.durationSeconds ?? raw.duration_seconds,
    sourceDocumentIds: raw.sourceDocumentIds ?? raw.source_document_ids,
    audioUrl: raw.audioUrl ?? raw.audio_url,
    depthLevel: raw.depthLevel ?? raw.depth_level,
  };
}

// Streams a podcast/audiobook generation. onStage fires for each progress stage.
// Resolves with the final result. Rejects on an error event or a missing result.
export async function generateAudioStream(
  payload: GenerateAudioPayload,
  onStage: (stage: string) => void,
): Promise<AudioGenerationResult> {
  const token = await ensureFreshAccessToken();
  const res = await fetch(`${AI_STREAMING_BASE_URL}/api/audio/generate-stream`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    credentials: 'include',
    body: JSON.stringify(payload),
  });

  const contentType = res.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    const data = await res.json();
    if (data?.error) throw new Error(data.error);
    return data as AudioGenerationResult;
  }
  if (!res.ok || !res.body) {
    throw new Error(`Generation failed (${res.status})`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let result: AudioGenerationResult | null = null;

  const handleFrame = (frame: string) => {
    const lines = frame.split('\n');
    let event = 'message';
    const dataLines: string[] = [];
    for (const line of lines) {
      if (line.startsWith('event:')) event = line.slice(6).trim();
      else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim());
    }
    if (dataLines.length === 0) return; // keepalive / comment
    let data: any;
    try {
      data = JSON.parse(dataLines.join('\n'));
    } catch {
      return;
    }
    if (event === 'stage' || data?.stage) onStage(data.stage);
    else if (event === 'result' || data?.stream_url || data?.audio_content_id) result = data;
    else if (event === 'error' || data?.error) throw new Error(data.error || 'Generation error');
  };

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let idx: number;
    while ((idx = buffer.indexOf('\n\n')) !== -1) {
      const frame = buffer.slice(0, idx);
      buffer = buffer.slice(idx + 2);
      handleFrame(frame);
    }
  }
  if (buffer.trim()) handleFrame(buffer);

  if (!result) throw new Error('Generation ended without a result');
  return result;
}

export interface TextToSpeechInput {
  text: string;
  voice: TtsVoice;
  signal?: AbortSignal;
}

// Direct-read: returns an MP3 blob. Nothing is persisted server-side.
export async function textToSpeech({ text, voice, signal }: TextToSpeechInput): Promise<Blob> {
  const token = await ensureFreshAccessToken();
  const res = await fetch(`${AI_SERVICE_BASE_URL}/api/audio/tts`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    credentials: 'include',
    signal,
    body: JSON.stringify({ text, voice }),
  });
  if (!res.ok) {
    const text2 = await res.text();
    let msg = `TTS failed (${res.status})`;
    try {
      msg = JSON.parse(text2)?.error || msg;
    } catch {
      /* ignore */
    }
    const err = new Error(msg) as Error & { status?: number };
    err.status = res.status;
    throw err;
  }
  const buf = await res.arrayBuffer();
  return new Blob([buf], { type: 'audio/mpeg' });
}

export interface ListAudioParams {
  format?: AudioFormat;
  // Filters to audio whose source documents include this id (server-side).
  documentId?: number;
  page?: number;
  size?: number;
}

export async function listAudioContent(params: ListAudioParams = {}): Promise<AudioContentItem[]> {
  const q = new URLSearchParams();
  q.set('page', String(params.page ?? 0));
  q.set('size', String(params.size ?? 100));
  q.set('sortBy', 'createdAt');
  q.set('sortDirection', 'DESC');
  if (params.format) q.set('format', params.format);
  if (params.documentId != null) q.set('documentId', String(params.documentId));

  const res = await fetchWithAuth(`/api/audio-content/list?${q.toString()}`);
  if (!res.ok) throw new Error(`Failed to load library (${res.status})`);
  const data = await res.json();
  const list = data.audioContents ?? data.content ?? data.items ?? (Array.isArray(data) ? data : []);
  return list.map(normalizeAudio);
}

export async function renameAudioContent(id: number, title: string, description?: string): Promise<void> {
  const res = await fetchWithAuth(`/api/audio-content/${id}`, {
    method: 'PUT',
    body: JSON.stringify({ title, description }),
  });
  if (!res.ok) throw new Error(`Rename failed (${res.status})`);
}

export async function deleteAudioContent(id: number): Promise<void> {
  const res = await fetchWithAuth(`/api/audio-content/${id}`, { method: 'DELETE' });
  if (!res.ok) throw new Error(`Delete failed (${res.status})`);
}

// Resolves a fresh, signed, playable stream URL for a generated audio's documentId.
export async function fetchStreamUrl(documentId: number, expiration = 3600): Promise<{ url: string; expiresAt?: number }> {
  const res = await fetchWithAuth(
    `${DATA_SERVICE_BASE_URL}/api/storage/audio/stream-url?documentId=${documentId}&expiration=${expiration}`,
  );
  if (!res.ok) throw new Error(`Failed to resolve stream URL (${res.status})`);
  const data = await res.json();
  const url = data.streamUrl ?? data.url;
  if (!url) throw new Error('No stream URL returned');
  const expiresAt = data.expiresAt ? new Date(data.expiresAt).getTime() : undefined;
  return { url, expiresAt };
}
